import React, { useEffect, useState } from 'react'
import { Badge, Button, Card, Container } from 'react-bootstrap'
import { withRouter } from 'react-router-dom'

import Moment from 'moment'

import FAIcon from '../components/FAIcon'
import LoadingSpinner from '../components/LoadingSpinner'
import { getToken } from '../utils/getToken'

const StandaloneDocumentViewer = ({ match }) => {
  const token = match?.params?.token
  const [metadata, setMetadata] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const isAuthenticated = Boolean(getToken())

  useEffect(() => {
    if (!token) {
      setError('Invalid document link.')
      setLoading(false)
      return
    }

    let isMounted = true
    const search = window.location.search || ''
    const searchParams = new URLSearchParams(search)
    const forceExpired =
      searchParams.get('expired') === '1' || String(searchParams.get('expired')).toLowerCase() === 'true'

    fetch(`/api/shared/files/${encodeURIComponent(token)}${search}`, {
      method: 'GET',
      headers: {
        Accept: 'application/json',
      },
    })
      .then(async res => {
        if (!res.ok || forceExpired) {
          if (res.status === 410 || forceExpired) {
            throw new Error('This shared document link has expired.')
          }
          throw new Error('This document link is invalid or has expired.')
        }
        return res.json()
      })
      .then(data => {
        if (!isMounted) return
        setMetadata({
          caseNumber: data.caseNumber || data.CaseNumber,
          fileName: data.fileName || data.FileName || 'Document',
          fileId: data.fileId || data.FileId,
          contentType: data.contentType || data.ContentType || 'application/octet-stream',
          expiresUtc: data.expiresUtc || data.ExpiresUtc,
        })
        setLoading(false)
      })
      .catch(err => {
        if (!isMounted) return
        setError(err.message || 'Unable to load shared document.')
        setLoading(false)
      })

    return () => {
      isMounted = false
    }
  }, [token])

  if (loading) {
    return (
      <Container className="py-5 text-center">
        <LoadingSpinner />
        <p className="text-muted mt-3">Verifying secure document link...</p>
      </Container>
    )
  }

  if (error || !metadata) {
    return (
      <Container className="py-5" style={{ maxWidth: '640px' }} data-testid="standalone-viewer-error">
        <Card className="shadow-sm border-0">
          <Card.Body className="text-center py-5">
            <FAIcon name="exclamation-triangle" className="fa-3x text-warning mb-3" />
            <h4 className="font-weight-bold">Document Link Unavailable</h4>
            <p className="text-muted mb-4">{error || 'This link may have expired or been revoked.'}</p>
            {isAuthenticated && (
              <a href="/cases" className="btn btn-primary">
                Return to Cases
              </a>
            )}
          </Card.Body>
        </Card>
      </Container>
    )
  }

  const contentUrl = `/api/shared/files/${encodeURIComponent(token)}/content`
  const downloadUrl = `${contentUrl}?download=true`
  const isPdf =
    metadata.contentType === 'application/pdf' ||
    String(metadata.fileName || '')
      .toLowerCase()
      .endsWith('.pdf')
  const isImage =
    String(metadata.contentType || '').startsWith('image/') ||
    /\.(png|jpe?g|gif|webp|bmp)$/i.test(metadata.fileName || '')

  return (
    <div className="min-vh-100 bg-light d-flex flex-column" data-testid="standalone-document-viewer">
      <div className="bg-white border-bottom shadow-sm py-3 px-4 d-flex justify-content-between align-items-center flex-wrap">
        <div className="d-flex align-items-center my-1">
          <FAIcon name="file-alt" className="fa-lg text-primary mr-3" />
          <div>
            <h5 className="mb-0 font-weight-bold text-break">{metadata.fileName}</h5>
            <div className="small text-muted">
              <span>GuideSmart Secure Document Viewer</span>
              {metadata.expiresUtc && (
                <Badge variant="light" className="ml-2 border">
                  Expires {Moment(metadata.expiresUtc).format('MM/DD/YYYY h:mm A')}
                </Badge>
              )}
            </div>
          </div>
        </div>
        <div className="d-flex align-items-center my-1" style={{ gap: '8px' }}>
          {isAuthenticated && metadata.caseNumber && (
            <a
              href={`/cases/${metadata.caseNumber}/documents?fileName=${encodeURIComponent(metadata.fileName)}`}
              className="btn btn-outline-primary btn-sm mr-2"
              data-testid="open-in-case-btn"
            >
              <FAIcon name="folder-open" className="mr-1" /> Open in Case #{metadata.caseNumber}
            </a>
          )}
          <Button
            as="a"
            href={downloadUrl}
            variant="primary"
            size="sm"
            download={metadata.fileName}
            data-testid="download-shared-file-btn"
          >
            <FAIcon name="download" className="mr-1" /> Download
          </Button>
        </div>
      </div>

      <div className="flex-grow-1 d-flex flex-column p-3">
        {isPdf ? (
          <iframe
            title={metadata.fileName}
            src={contentUrl}
            className="w-100 flex-grow-1 border rounded bg-white shadow-sm"
            style={{ minHeight: '80vh' }}
          />
        ) : isImage ? (
          <div className="text-center bg-white p-4 border rounded shadow-sm">
            <img
              src={contentUrl}
              alt={metadata.fileName}
              className="img-fluid"
              style={{ maxHeight: '80vh', objectFit: 'contain' }}
            />
          </div>
        ) : (
          <Container className="py-5" style={{ maxWidth: '600px' }}>
            <Card className="shadow-sm border-0 text-center p-4">
              <Card.Body>
                <FAIcon name="file-download" className="fa-3x text-primary mb-3" />
                <h5 className="font-weight-bold mb-2">{metadata.fileName}</h5>
                <p className="text-muted small mb-4">
                  This file format cannot be previewed directly in the browser. Click below to download a copy.
                </p>
                <Button as="a" href={downloadUrl} variant="primary" download={metadata.fileName}>
                  <FAIcon name="download" className="mr-2" /> Download File
                </Button>
              </Card.Body>
            </Card>
          </Container>
        )}
      </div>
    </div>
  )
}

export default withRouter(StandaloneDocumentViewer)
